// @flow

import React, { PureComponent } from 'react';
import qs from 'qs';
import debounce from 'lodash.debounce';
import { Map } from 'immutable';
import { graphql, QueryRenderer } from 'react-relay';
import { i18n, Analytics } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';
import AddUpdateOrderModal from 'main-app/components/AddUpdateOrderModal';
import ExportCSVModal from 'main-app/components/ExportCSVModal';
import FilterControls, {
  type FilterValue,
} from 'main-app/components/FilterControls';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import type { RouterHistory } from 'react-router';
import getSortOptions from './getSortOptions';

const UploadPOButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background: #f0f0f0;
  color: #333;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  margin-right: 12px;
  &:hover {
    background: #e0e0e0;
  }
`;
import getFilterOptions from './getFilterOptions';
import PaginatedOrdersContainer from './PaginatedOrdersContainer';

type Props = {
  history: RouterHistory,
};

type State = {
  addModalOpen: boolean,
  exportModalOpen: boolean,
  search: string,
  graphqlSearchValue: string,
  createAnotherOrder: boolean,
};

const DEFAULT_FILTER = {
  sort: 'CREATED_AT',
  direction: 'DESC',
};

class Orders extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      addModalOpen: false,
      exportModalOpen: false,
      search: '',
      graphqlSearchValue: '',
      createAnotherOrder: false,
    };

    this.handleDebouncedSearch = debounce(this.handleDebouncedSearch, 500);
  }

  getFilters = (): FilterValue => {
    const { history } = this.props;
    const { search } = history.location;

    if (!search) {
      return DEFAULT_FILTER;
    }

    return {
      ...DEFAULT_FILTER,
      ...qs.parse(search, { ignoreQueryPrefix: true }),
    };
  };

  setFilters = (filters: Object) => {
    const { history } = this.props;

    history.replace(
      '/orders?' +
        qs.stringify(filters, {
          encode: true,
          arrayFormat: 'brackets',
        }),
    );
  };

  filtersDirty = () => {
    const { graphqlSearchValue } = this.state;

    return (
      !Map(this.getFilters()).equals(Map(DEFAULT_FILTER)) || graphqlSearchValue
    );
  };

  compileQueryVariables = () => {
    const filters = this.getFilters();
    const { graphqlSearchValue } = this.state;

    const queryVariables = {
      ...filters,
      search: graphqlSearchValue.trim() || null,
      sortBy: {
        field: filters.sort,
        direction: filters.direction,
      },
    };

    if (queryVariables.orderedAt) {
      const [start, end] = queryVariables.orderedAt.split(' - ');
      queryVariables.orderedAtStart = start;
      queryVariables.orderedAtEnd = end;

      delete queryVariables.orderedAt;
    }

    if (queryVariables.requiredAt) {
      const [start, end] = queryVariables.requiredAt.split(' - ');
      queryVariables.requiredAtStart = start;
      queryVariables.requiredAtEnd = end;

      delete queryVariables.requiredAt;
    }

    if (queryVariables.promisedAt) {
      const [start, end] = queryVariables.promisedAt.split(' - ');
      queryVariables.promisedAtStart = start;
      queryVariables.promisedAtEnd = end;

      delete queryVariables.promisedAt;
    }

    delete queryVariables.sort;
    delete queryVariables.direction;

    return queryVariables;
  };

  handleSearchInput = (search: string) => {
    this.setState(
      {
        search,
      },
      this.handleDebouncedSearch,
    );
  };

  handleDebouncedSearch = () => {
    const { search } = this.state;

    this.setState({
      graphqlSearchValue: search,
    });

    Analytics.trackEvent('Search Applied', {
      listName: 'Orders',
      searchString: search,
    });
  };

  handleCSVExport = async (columns: string[]) => {
    const gqlQueryArgs = this.compileQueryVariables();

    Analytics.trackEvent('Export List', {
      page: 'Order List',
    });
    window.open(
      `/api/v1/csv-export-orders?` +
        qs.stringify(
          {
            gqlQueryArgs,
            columns,
          },
          {
            encode: true,
            arrayFormat: 'brackets',
          },
        ),
    );
  };

  handleOpenCreateModal = () => {
    this.setState({
      addModalOpen: true,
    });
  };

  handleOpenExportModal = () => {
    this.setState({
      exportModalOpen: true,
    });
  };

  handleCloseAllModals = () => {
    this.setState({
      addModalOpen: false,
      exportModalOpen: false,
      createAnotherOrder: false,
    });
  };

  handleAddModalReopen = () => {
    this.setState(
      {
        addModalOpen: false,
        exportModalOpen: false,
        createAnotherOrder: true,
      },
      () => {
        this.setState({
          addModalOpen: true,
        });
      },
    );
  };

  render() {
    const { history } = this.props;
    const {
      search,
      addModalOpen,
      exportModalOpen,
      createAnotherOrder,
    } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{
          force: true,
        }}
        query={graphql`
          query OrdersQuery(
            $count: Int!
            $cursor: String
            $search: String
            $sortBy: OrderSort
            $orderNumbers: [String!]
            $customerOrderNumbers: [String!]
            $customerIds: [ID!]
            $salesUserIds: [ID!]
            $statuses: [OrderStatus!]
            $promisedAtStart: Date
            $promisedAtEnd: Date
            $orderedAtStart: Date
            $orderedAtEnd: Date
            $requiredAtStart: Date
            $requiredAtEnd: Date
          ) {
            ...PaginatedOrdersContainer_orders
              @arguments(
                search: $search
                sortBy: $sortBy
                orderNumbers: $orderNumbers
                customerOrderNumbers: $customerOrderNumbers
                customerIds: $customerIds
                salesUserIds: $salesUserIds
                statuses: $statuses
                promisedAtStart: $promisedAtStart
                promisedAtEnd: $promisedAtEnd
                orderedAtStart: $orderedAtStart
                orderedAtEnd: $orderedAtEnd
                requiredAtStart: $requiredAtStart
                requiredAtEnd: $requiredAtEnd
              )

            viewer {
              id
              permissions
              company {
                id
              }
            }
          }
        `}
        variables={{
          ...this.compileQueryVariables(),
          count: 30,
          cursor: null,
        }}
        render={({ props }) => (
          <Page>
            <PageHeaderList
              title={i18n.t('Orders')}
              left={
                <UploadPOButton to="/po-upload">
                  {i18n.t('Upload PO')}
                </UploadPOButton>
              }
              exportButtonLabel={props && i18n.t('Export to CSV')}
              onExportButtonClick={this.handleOpenExportModal}
              createButtonLabel={
                props &&
                props.viewer.permissions.includes('CREATE_ORDER') &&
                i18n.t('New Order')
              }
              onCreateButtonClick={this.handleOpenCreateModal}
              searchPlaceholder={i18n.t('Customer Name or Order #')}
              searchValue={search}
              onSearchChange={this.handleSearchInput}
            />
            <FilterControls
              value={this.getFilters()}
              defaultValue={DEFAULT_FILTER}
              sortOptions={getSortOptions()}
              filterOptions={getFilterOptions()}
              savedFilterType="ORDER"
              onFiltersChange={filters => this.setFilters(filters)}
            />
            {!props ? (
              <Loader />
            ) : (
              <PaginatedOrdersContainer
                history={history}
                filtersDirty={this.filtersDirty()}
                onCreateNew={this.handleOpenCreateModal}
                orders={props}
                deleteEnabled
                editEnabled={props.viewer.permissions.includes('UPDATE_ORDER')}
              />
            )}
            {addModalOpen && (
              <AddUpdateOrderModal
                onClose={this.handleCloseAllModals}
                onSuccess={order => history.push(`/order/${order.id}`)}
                onReopen={this.handleAddModalReopen}
                createAnotherOrder={createAnotherOrder}
                order={null}
                customer={null}
                companyId={props.viewer.company.id}
              />
            )}
            {exportModalOpen && (
              <ExportCSVModal
                onClose={this.handleCloseAllModals}
                title={i18n.t('Export Orders to CSV')}
                columnsApiUrl="/api/v1/csv-export-orders/columns"
                onSubmit={this.handleCSVExport}
              />
            )}
          </Page>
        )}
      />
    );
  }
}

export default Orders;
